import React from 'react'
import { Box, Skeleton, styled } from '@mui/material'
import { AvatarStackItem } from '@/design-system'
import { ProfileAvatarRing } from '../ProfileAvatarRing/ProfileAvatarRing'
import { ProfileIdBlock } from '../ProfileIdBlock/ProfileIdBlock'
import { ProfileGlassBar, ProfileGlassBarStat } from '../ProfileGlassBar/ProfileGlassBar'
import { ProfileHeroActions, ConnectState } from '../ProfileHeroActions/ProfileHeroActions'

const HERO_HEIGHT = 546
const GLASSBAR_HEIGHT = 82

const Root = styled(Box)({
  position: 'relative',
  width: '100%',
  height: HERO_HEIGHT,
  borderRadius: 'var(--ds-radius-xl)',
  overflow: 'hidden',
  background: 'var(--ds-night-canvas)',
  isolation: 'isolate',
})

const Mosaic = styled(Box)({
  position: 'absolute',
  inset: 0,
  display: 'grid',
  gridTemplateColumns: 'repeat(6, 1fr)',
  gridTemplateRows: 'repeat(2, 1fr)',
  gap: 2,
})

const MosaicTile = styled('div')({
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  filter: 'saturate(0.55) brightness(0.62)',
  transform: 'scale(1.02)',
})

const ColorWash = styled(Box)({
  position: 'absolute',
  inset: 0,
  zIndex: 1,
  mixBlendMode: 'screen',
  background: `
    radial-gradient(ellipse 60% 70% at 88% 4%, color-mix(in srgb, var(--ds-accent-dot-4) 40%, transparent), transparent 58%),
    radial-gradient(ellipse 55% 60% at 4% 96%, color-mix(in srgb, var(--ds-accent-dot-1) 32%, transparent), transparent 58%),
    radial-gradient(ellipse 45% 50% at 50% 40%, color-mix(in srgb, var(--ds-accent-dot-3) 18%, transparent), transparent 62%)
  `,
})

const Scrim = styled(Box)({
  position: 'absolute',
  inset: 0,
  zIndex: 1,
  background: `linear-gradient(180deg,
    color-mix(in srgb, var(--ds-night-canvas) 28%, transparent) 0%,
    color-mix(in srgb, var(--ds-night-canvas) 36%, transparent) 34%,
    color-mix(in srgb, var(--ds-night-canvas) 86%, transparent) 82%,
    color-mix(in srgb, var(--ds-night-canvas) 97%, transparent) 100%)`,
})

const Topbar = styled(Box)({
  position: 'absolute',
  top: 18,
  left: 22,
  right: 22,
  zIndex: 3,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
})

const BackLink = styled('button')({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  height: 32,
  padding: '0 14px',
  borderRadius: 'var(--ds-radius-pill)',
  background: 'color-mix(in srgb, var(--ds-night-canvas) 40%, transparent)',
  backdropFilter: 'blur(10px)',
  boxShadow: 'inset 0 0 0 1px color-mix(in srgb, var(--ds-night-ink) 14%, transparent)',
  fontSize: 12,
  fontWeight: 600,
  color: 'color-mix(in srgb, var(--ds-night-ink) 88%, transparent)',
  border: 'none',
  cursor: 'pointer',
  transition: 'all 140ms ease',
  '&:hover': {
    background: 'color-mix(in srgb, var(--ds-night-canvas) 60%, transparent)',
    color: 'var(--ds-night-ink)',
  },
})

const Body = styled(Box)({
  position: 'absolute',
  left: 0,
  right: 0,
  bottom: GLASSBAR_HEIGHT,
  zIndex: 3,
  display: 'flex',
  alignItems: 'flex-end',
  padding: '0 36px 26px',
})

const Grid = styled(Box)({
  display: 'flex',
  alignItems: 'flex-end',
  justifyContent: 'space-between',
  gap: 28,
  width: '100%',
})

const LeftCluster = styled(Box)({
  display: 'flex',
  alignItems: 'flex-end',
  gap: 24,
  minWidth: 0,
})

export interface ProfileHeroWallProps {
  firstName: string
  lastName: string
  userName?: string
  profession?: string
  location?: string
  motto?: string
  joinYear?: string
  avatarUrl?: string
  mosaicImages?: string[]
  isPro?: boolean
  galleries: number
  connections: number
  albums?: number
  isSelf: boolean
  connectState: ConnectState
  mutuals?: AvatarStackItem[]
  mutualCount?: number
  onBack?: () => void
  onConnect?: () => void
  onAccept?: () => void
  onDecline?: () => void
  onMessage?: () => void
  onEditProfile?: () => void
  onViewConnections?: () => void
  onGalleriesStatClick?: () => void
  onConnectionsStatClick?: () => void
  onMutualsClick?: () => void
  actionLoading?: boolean
  loading?: boolean
}

export function ProfileHeroWall({
  firstName,
  lastName,
  userName,
  profession,
  location,
  motto,
  joinYear,
  avatarUrl,
  mosaicImages,
  isPro,
  galleries,
  connections,
  albums,
  isSelf,
  connectState,
  mutuals,
  mutualCount,
  onBack,
  onConnect,
  onAccept,
  onDecline,
  onMessage,
  onEditProfile,
  onViewConnections,
  onGalleriesStatClick,
  onConnectionsStatClick,
  onMutualsClick,
  actionLoading,
  loading,
}: ProfileHeroWallProps) {
  if (loading) {
    return <Skeleton variant='rectangular' sx={{ height: HERO_HEIGHT, borderRadius: 'var(--ds-radius-xl)' }} />
  }

  const tiles = mosaicImages && mosaicImages.length > 0
    ? Array.from({ length: 12 }, (_, i) => mosaicImages[i % mosaicImages.length])
    : []

  const stats: ProfileGlassBarStat[] = [
    { value: galleries, label: 'GALLERIES', onClick: onGalleriesStatClick },
    ...(albums != null ? [{ value: albums, label: 'ALBUMS' }] : []),
    { value: connections, label: 'CONNECTIONS', onClick: onConnectionsStatClick },
  ]

  return (
    <Root>
      {tiles.length > 0 ? (
        <Mosaic aria-hidden>
          {tiles.map((src, i) => (
            <MosaicTile key={i} style={{ backgroundImage: `url(${src})` }} />
          ))}
        </Mosaic>
      ) : (
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            inset: 0,
            background: `
              radial-gradient(ellipse 55% 65% at 12% 20%, color-mix(in srgb, var(--ds-accent-dot-1) 42%, transparent), transparent 58%),
              radial-gradient(ellipse 50% 55% at 88% 15%, color-mix(in srgb, var(--ds-accent-dot-4) 36%, transparent), transparent 55%),
              radial-gradient(ellipse 60% 50% at 55% 90%, color-mix(in srgb, var(--ds-accent-dot-2) 28%, transparent), transparent 60%),
              linear-gradient(155deg, #1c1824 0%, var(--ds-night-canvas) 48%, #121018 100%)
            `,
          }}
        />
      )}
      <ColorWash aria-hidden />
      <Scrim aria-hidden />

      {onBack && (
        <Topbar>
          <BackLink onClick={onBack}>← Connections</BackLink>
        </Topbar>
      )}

      <Body>
        <Grid>
          <LeftCluster>
            <ProfileAvatarRing name={`${firstName} ${lastName}`} avatarUrl={avatarUrl} />
            <ProfileIdBlock
              firstName={firstName}
              lastName={lastName}
              userName={userName}
              profession={profession}
              location={location}
              motto={motto}
              joinYear={joinYear}
              galleryCount={galleries}
              isPro={isPro}
            />
          </LeftCluster>

          <ProfileHeroActions
            isSelf={isSelf}
            connectState={connectState}
            onConnect={onConnect}
            onAccept={onAccept}
            onDecline={onDecline}
            onMessage={onMessage}
            onEditProfile={onEditProfile}
            onViewConnections={onViewConnections}
            loading={actionLoading}
          />
        </Grid>
      </Body>

      <ProfileGlassBar mutuals={mutuals} mutualCount={mutualCount} onMutualsClick={onMutualsClick} stats={stats} />
    </Root>
  )
}
